import { useState, useEffect, useRef, Fragment } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Wallet, Search, ClipboardList, Copy, Check, Calendar, Building2, Undo2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { formatLocalDate } from '@/lib/utils';

interface Empresa {
  id: number;
  profileName: string;
  legalName: string;
  rfc: string;
  isDefault: boolean;
}

interface AvailablePayment {
  id: number;
  invoiceNumber: string;
  customerName: string;
  amount: string;
  paymentMethod: string;
  paymentDate: string;
  serieName?: string | null;
  invoicingSettingsId?: number | null;
  folioSeriesId?: number | null;
  folio?: number | null;
}

interface CashCut {
  id: number;
  dateFrom: string;
  dateTo: string;
  totalAmount: string;
  paymentCount: number;
  notes: string | null;
  createdByName: string | null;
  createdAt: string;
  status: 'active' | 'voided';
}

interface CashCutSummaryRow {
  invoicingSettingsId: number | null;
  empresaName: string;
  totalAmount: string;
  paymentCount: number;
}

const paymentMethodLabels: Record<string, string> = {
  TRANSFER: 'Transferencia Bancaria',
  CASH: 'Efectivo',
  CHECK: 'Cheque',
  CARD: 'Tarjeta',
  OTHER: 'Otro',
};

function formatDateShort(dateStr: string) {
  return formatLocalDate(dateStr, { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function formatCurrency(amount: string | number) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(amount));
}

export default function CashCuts() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new');
  const [empresas, setEmpresas] = useState<Empresa[]>([]);

  useEffect(() => {
    fetch('/api/finance/emisores', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setEmpresas(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // ── Nuevo corte ──────────────────────────────────────────────────────────
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [methodFilters, setMethodFilters] = useState<string[]>([]);
  const [empresaFilter, setEmpresaFilter] = useState<string>('all');
  const [folioFrom, setFolioFrom] = useState('');
  const [folioTo, setFolioTo] = useState('');
  const [available, setAvailable] = useState<AvailablePayment[]>([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);

  const searchAvailable = async () => {
    setLoadingAvailable(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (methodFilters.length > 0) params.set('paymentMethods', methodFilters.join(','));
      if (empresaFilter !== 'all') params.set('empresaId', empresaFilter);
      if (folioFrom) params.set('folioFrom', folioFrom);
      if (folioTo) params.set('folioTo', folioTo);
      const res = await fetch(`/api/treasury/cash-cuts/available-payments?${params}`, { credentials: 'include' });
      if (res.ok) {
        setAvailable(await res.json());
        setSelected(new Set());
      }
    } finally {
      setLoadingAvailable(false);
    }
  };

  useEffect(() => { searchAvailable(); }, []);

  const toggleOne = (id: number, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelected(checked ? new Set(available.map((p) => p.id)) : new Set());
  };

  const selectedPayments = available.filter((p) => selected.has(p.id));
  const selectedTotal = selectedPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);

  const [dateError, setDateError] = useState(false);
  const dateFromRef = useRef<HTMLInputElement>(null);

  const generateCut = async () => {
    if (selected.size === 0) {
      toast({ title: 'Selecciona al menos un pago', variant: 'destructive' });
      return;
    }
    if (!dateFrom || !dateTo) {
      setDateError(true);
      toast({ title: 'Indica el rango de fechas del corte', description: 'Completa los campos De y A.', variant: 'destructive' });
      dateFromRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      dateFromRef.current?.focus();
      return;
    }
    setDateError(false);
    setGenerating(true);
    try {
      const res = await fetch('/api/treasury/cash-cuts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          paymentIds: Array.from(selected),
          dateFrom,
          dateTo,
          paymentMethods: methodFilters.length > 0 ? methodFilters : undefined,
          invoicingSettingsId: empresaFilter !== 'all' ? Number(empresaFilter) : undefined,
          notes: notes || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al generar el corte');
      }
      const cut = await res.json();
      toast({ title: `Corte #${cut.id} generado`, description: `${cut.paymentCount} pagos por ${formatCurrency(cut.totalAmount)}` });
      setNotes('');
      await searchAvailable();
      await loadHistory();
      setActiveTab('history');
      setOpenCutId(cut.id);
      await loadDetail(cut.id);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const voidCut = async (cut: CashCut) => {
    if (!confirm(`¿Cancelar el corte #${cut.id}? Sus ${cut.paymentCount} pago(s) quedarán disponibles para otro corte.`)) return;
    const reason = window.prompt('Motivo de la cancelación (opcional):') ?? undefined;
    try {
      const res = await fetch(`/api/treasury/cash-cuts/${cut.id}/void`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: reason || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cancelar el corte');
      }
      toast({ title: `Corte #${cut.id} cancelado`, description: 'Sus pagos ya están disponibles de nuevo.' });
      await loadHistory();
      await searchAvailable();
      if (openCutId === cut.id) setOpenCutId(null);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  // ── Historial ────────────────────────────────────────────────────────────
  const [history, setHistory] = useState<CashCut[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [openCutId, setOpenCutId] = useState<number | null>(null);
  const [detail, setDetail] = useState<{ cut: CashCut; summary: CashCutSummaryRow[] } | null>(null);
  const [copied, setCopied] = useState(false);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch('/api/treasury/cash-cuts', { credentials: 'include' });
      if (res.ok) setHistory(await res.json());
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const loadDetail = async (id: number) => {
    const res = await fetch(`/api/treasury/cash-cuts/${id}`, { credentials: 'include' });
    if (res.ok) {
      const body = await res.json();
      const { summary, ...cut } = body;
      setDetail({ cut, summary });
    }
  };

  const toggleDetail = async (id: number) => {
    if (openCutId === id) {
      setOpenCutId(null);
      setDetail(null);
      return;
    }
    setOpenCutId(id);
    setCopied(false);
    await loadDetail(id);
  };

  const copySummary = () => {
    if (!detail) return;
    const lines = [
      ['Razón Social', 'Pagos', 'Monto'].join('\t'),
      ...detail.summary.map((s) => [s.empresaName, String(s.paymentCount), Number(s.totalAmount).toFixed(2)].join('\t')),
      ['Total', String(detail.cut.paymentCount), Number(detail.cut.totalAmount).toFixed(2)].join('\t'),
    ];
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Wallet className="h-6 w-6 text-primary" />
        <h1 className="text-xl font-semibold">Corte de Caja</h1>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'new' | 'history')}>
        <TabsList>
          <TabsTrigger value="new">Nuevo Corte</TabsTrigger>
          <TabsTrigger value="history">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="new">
          <Card className="mt-3">
            <CardHeader className="pb-3">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-xs whitespace-nowrap">De</Label>
                  <Input ref={dateFromRef} type="date" className={`w-40 ${dateError && !dateFrom ? 'border-red-500 ring-1 ring-red-500' : ''}`} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                  <Label className="text-xs whitespace-nowrap">A</Label>
                  <Input type="date" className={`w-40 ${dateError && !dateTo ? 'border-red-500 ring-1 ring-red-500' : ''}`} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
                {dateError && (!dateFrom || !dateTo) && (
                  <p className="text-sm font-medium text-red-600 self-center">Debes indicar las fechas De y A para generar el corte.</p>
                )}
                <div className="flex items-center gap-2">
                  <Label className="text-xs whitespace-nowrap">Método(s) de pago</Label>
                  <select
                    multiple
                    className="border rounded px-2 py-1 text-sm min-w-[160px]"
                    value={methodFilters}
                    onChange={(e) => setMethodFilters(Array.from(e.target.selectedOptions).map((o) => o.value))}
                  >
                    <option value="TRANSFER">Transferencia</option>
                    <option value="CASH">Efectivo</option>
                    <option value="CHECK">Cheque</option>
                    <option value="CARD">Tarjeta</option>
                    <option value="OTHER">Otro</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Folio</Label>
                  <Input className="w-20" inputMode="numeric" placeholder="De" value={folioFrom} onChange={(e) => setFolioFrom(e.target.value.replace(/\D/g, ''))} />
                  <Input className="w-20" inputMode="numeric" placeholder="A" value={folioTo} onChange={(e) => setFolioTo(e.target.value.replace(/\D/g, ''))} />
                </div>
                {empresas.length > 0 && (
                  <Select value={empresaFilter} onValueChange={setEmpresaFilter}>
                    <SelectTrigger className="w-[220px]"><SelectValue placeholder="Razón Social" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las empresas</SelectItem>
                      {empresas.map((e) => (
                        <SelectItem key={e.id} value={String(e.id)}>{e.profileName || e.legalName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button onClick={searchAvailable} disabled={loadingAvailable} size="sm">
                  <Search className="h-4 w-4 mr-1.5" />
                  {loadingAvailable ? 'Buscando...' : 'Buscar pagos'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-auto border rounded-lg max-h-[55vh]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-muted">
                    <tr className="border-b bg-muted">
                      <th className="text-center py-2.5 px-3 w-10">
                        <Checkbox
                          checked={available.length > 0 && selected.size === available.length}
                          onCheckedChange={toggleAll}
                        />
                      </th>
                      <th className="text-left py-2.5 px-3 font-medium">Folio</th>
                      <th className="text-left py-2.5 px-3 font-medium">Fecha</th>
                      <th className="text-left py-2.5 px-3 font-medium">Cliente</th>
                      <th className="text-left py-2.5 px-3 font-medium">Método</th>
                      <th className="text-right py-2.5 px-3 font-medium">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {available.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">
                        {loadingAvailable ? 'Cargando...' : 'No hay pagos disponibles para cortar con estos filtros'}
                      </td></tr>
                    )}
                    {available.map((p) => (
                      <tr key={p.id} className="border-b hover:bg-muted/30">
                        <td className="py-2 px-3 text-center">
                          <Checkbox checked={selected.has(p.id)} onCheckedChange={(c) => toggleOne(p.id, c)} />
                        </td>
                        <td className="py-2 px-3 font-mono text-xs">
                          {p.folioSeriesId ? `RS${p.invoicingSettingsId}-${p.serieName}-${String(p.folio).padStart(3, '0')}` : '—'}
                        </td>
                        <td className="py-2 px-3 whitespace-nowrap">{formatDateShort(p.paymentDate)}</td>
                        <td className="py-2 px-3">{p.customerName}</td>
                        <td className="py-2 px-3 text-xs">{paymentMethodLabels[p.paymentMethod] ?? p.paymentMethod}</td>
                        <td className="py-2 px-3 text-right font-medium">{formatCurrency(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="sticky bottom-0 z-10 mt-4 flex flex-col md:flex-row md:items-end gap-3 justify-between border-t bg-background pt-4 pb-3 shadow-[0_-4px_8px_-6px_rgba(0,0,0,0.2)]">
                <div className="flex-1">
                  <Label className="text-xs mb-1 block">Notas del corte (opcional)</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Ej. Corte semanal de efectivo, arqueo del turno matutino..." />
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="text-sm text-muted-foreground">{selected.size} pago(s) seleccionado(s)</div>
                  <div className="text-lg font-semibold text-green-700">{formatCurrency(selectedTotal)}</div>
                  <Button className="mt-2" onClick={generateCut} disabled={generating || selected.size === 0}>
                    {generating ? 'Generando...' : 'Generar Corte'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="mt-3">
            <CardContent className="pt-4">
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left py-2.5 px-3 font-medium">Corte</th>
                      <th className="text-left py-2.5 px-3 font-medium">Rango</th>
                      <th className="text-left py-2.5 px-3 font-medium">Hecho por</th>
                      <th className="text-center py-2.5 px-3 font-medium">Pagos</th>
                      <th className="text-right py-2.5 px-3 font-medium">Monto</th>
                      <th className="text-center py-2.5 px-3 font-medium">Resumen</th>
                      <th className="text-center py-2.5 px-3 font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 && (
                      <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">
                        {loadingHistory ? 'Cargando...' : 'Aún no se ha generado ningún corte'}
                      </td></tr>
                    )}
                    {history.map((c) => (
                      <Fragment key={c.id}>
                        <tr className="border-b hover:bg-muted/30">
                          <td className="py-2 px-3 font-mono text-xs">
                            #{c.id}
                            {c.status === 'voided' && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-sans font-medium text-red-700">Cancelado</span>}
                          </td>
                          <td className="py-2 px-3 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              {formatDateShort(c.dateFrom)} – {formatDateShort(c.dateTo)}
                            </div>
                          </td>
                          <td className="py-2 px-3 text-xs">{c.createdByName || '—'}</td>
                          <td className="py-2 px-3 text-center">{c.paymentCount}</td>
                          <td className="py-2 px-3 text-right font-medium">{formatCurrency(c.totalAmount)}</td>
                          <td className="py-2 px-3 text-center">
                            <Button variant="ghost" size="sm" onClick={() => toggleDetail(c.id)}>
                              <ClipboardList className="h-4 w-4" />
                            </Button>
                          </td>
                          <td className="py-2 px-3 text-center">
                            {c.status !== 'voided' && (
                              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => voidCut(c)}>
                                <Undo2 className="h-4 w-4 mr-1" /> Cancelar
                              </Button>
                            )}
                          </td>
                        </tr>
                        {openCutId === c.id && detail && (
                          <tr key={`${c.id}-detail`}>
                            <td colSpan={7} className="bg-muted/20 p-4">
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-sm font-medium flex items-center gap-1.5">
                                  <Building2 className="h-4 w-4" /> Resumen por Razón Social
                                </div>
                                <Button variant="outline" size="sm" onClick={copySummary}>
                                  {copied ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                                  {copied ? 'Copiado' : 'Copiar para Excel'}
                                </Button>
                              </div>
                              <table className="w-full text-sm bg-background rounded border">
                                <thead>
                                  <tr className="border-b bg-muted/40">
                                    <th className="text-left py-2 px-3 font-medium">Razón Social</th>
                                    <th className="text-center py-2 px-3 font-medium">Pagos</th>
                                    <th className="text-right py-2 px-3 font-medium">Monto</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detail.summary.map((s) => (
                                    <tr key={s.invoicingSettingsId ?? 'none'} className="border-b">
                                      <td className="py-2 px-3">{s.empresaName}</td>
                                      <td className="py-2 px-3 text-center">{s.paymentCount}</td>
                                      <td className="py-2 px-3 text-right">{formatCurrency(s.totalAmount)}</td>
                                    </tr>
                                  ))}
                                  <tr className="font-semibold">
                                    <td className="py-2 px-3">Total</td>
                                    <td className="py-2 px-3 text-center">{detail.cut.paymentCount}</td>
                                    <td className="py-2 px-3 text-right">{formatCurrency(detail.cut.totalAmount)}</td>
                                  </tr>
                                </tbody>
                              </table>
                              {detail.cut.notes && (
                                <p className="text-xs text-muted-foreground mt-2">Notas: {detail.cut.notes}</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
